// Ubicación en el repo: src/lib/receipts.js
// HTML de impresión de Agenda — replica fielmente printAppointmentReceipt()
// y printDayAgenda() de prototipo-actual.html (líneas ~1036-1096): mismo
// membrete "AGENDA COMUNAL · by GlobalVision", misma estructura de tabla.
// ("GlobalHearing" se sacó del membrete el 23-ago-2026 — ver DECISIONS.md
// #96, los imprimibles/legales van solo con GlobalVision.)
//
// DESVÍO respecto al prototipo (DECISIONS.md #39): el prototipo nunca
// reservó espacio para logo en estos dos documentos (a diferencia del
// informe de audiometría y la receta, que sí traían el grid de 3 columnas
// vacío desde el principio, ver DECISIONS.md #28). Como el usuario pidió
// explícitamente que el logo de sucursal aparezca también en el
// comprobante de cita y la agenda del día, acá se agrega ese mismo grid
// por primera vez — "pequeño cambio" respecto al prototipo, no un
// rediseño del documento.
import { esc } from './print';
import { formatCurrency, formatDateLong, formatPatientName, formatProfessionalName } from './format';
import { addressHtml } from './mapsLink';

export function letterhead(logoUrl) {
  return `
    <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; margin-bottom:18px;">
      <div>${logoUrl ? `<img src="${esc(logoUrl)}" style="max-height:36px; max-width:110px; object-fit:contain;" />` : ''}</div>
      <div style="text-align:center;">
        <h2 style="font-family:'Space Grotesk',Arial,sans-serif; margin:0;">AGENDA COMUNAL</h2>
        <div style="color:#666; font-size:11px;">by GlobalVision</div>
      </div>
      <div></div>
    </div>
  `;
}

export function appointmentReceiptHTML(appt, branch, logoUrl) {
  const patientName = appt.patients ? formatPatientName(appt.patients) : '—';
  return `
    <div style="font-family:'Inter',Arial,sans-serif; padding:30px; max-width:480px; border:2px solid #333; border-radius:10px; margin:20px auto;">
      ${letterhead(logoUrl)}
      <h3 style="text-align:center; font-size:14px; text-transform:uppercase; letter-spacing:.05em; border-top:1px solid #ccc; border-bottom:1px solid #ccc; padding:8px 0; margin:12px 0;">Comprobante de cita</h3>
      <table style="width:100%; font-size:13px; border-collapse:collapse;">
        <tr><td style="padding:5px 0; color:#666;">Paciente</td><td style="padding:5px 0; font-weight:700; text-align:right;">${esc(patientName)}</td></tr>
        <tr><td style="padding:5px 0; color:#666;">Fecha</td><td style="padding:5px 0; font-weight:700; text-align:right; text-transform:capitalize;">${esc(formatDateLong(appt.date))}</td></tr>
        <tr><td style="padding:5px 0; color:#666;">Hora</td><td style="padding:5px 0; font-weight:700; text-align:right;">${esc(appt.time?.slice(0, 5))} hrs</td></tr>
        <tr><td style="padding:5px 0; color:#666;">Tipo de atención</td><td style="padding:5px 0; font-weight:700; text-align:right;">${esc(appt.type)}</td></tr>
        <tr><td style="padding:5px 0; color:#666;">Profesional</td><td style="padding:5px 0; font-weight:700; text-align:right;">${esc(formatProfessionalName(appt.professionals, { withRole: true }))}</td></tr>
        <tr><td style="padding:5px 0; color:#666;">Sucursal</td><td style="padding:5px 0; font-weight:700; text-align:right;">${esc(branch?.name || '—')}</td></tr>
        ${branch?.address ? `<tr><td style="padding:5px 0; color:#666;">Dirección</td><td style="padding:5px 0; font-weight:700; text-align:right;">${addressHtml(branch.address, branch?.mapsLinkEnabled ?? branch?.maps_link_enabled)}</td></tr>` : ''}
      </table>
      <p style="font-size:11.5px; color:#666; margin-top:18px; text-align:center;">Por favor preséntese 10 minutos antes de su hora agendada.</p>
    </div>
  `;
}

// Cotización de audífonos (migración 0051, 26-ago-2026) — pedido de
// Nicolás: uno o varios modelos con precio, para un paciente con ficha o
// un prospecto sin ella. `items` ya viene con `imageUrl` resuelta por el
// llamador (signed URL de Storage, ver QuoteFormModal.jsx/QuotesList.jsx)
// — este archivo solo arma el HTML, no habla con Supabase directamente
// (mismo criterio que el resto de receipts.js).
//
// Rediseño (11-sep-2026, DECISIONS.md #112): pedido explícito de Nicolás
// de una cotización "más elegante" y de una sola hoja A4, sin achicar la
// letra — el problema del formato anterior era que las "tarjetas" con
// borde y harto padding por ítem desperdiciaban espacio. Se toma como
// referencia de estilo examReport.jsx: ancho completo (max-width:210mm,
// igual al ancho de la hoja) en vez de un recuadro angosto centrado,
// datos del paciente en una grilla compacta de 2 columnas en vez de una
// tabla de una columna con mucho padding, e ítems en filas de tabla
// (con miniatura de foto) en vez de tarjetas con borde individual. Se
// agrega también un total y los datos de contacto de la sucursal al pie,
// para que el documento se sienta más cuidado/personalizado.
export function hearingaidQuoteHTML(quote, items, logoUrl) {
  const isProspect = !quote.patient_id;
  const contactName = isProspect ? quote.prospect_name : formatPatientName(quote.patients);
  const rut = !isProspect ? quote.patients?.rut : '';
  const phone = isProspect ? quote.prospect_phone : quote.patients?.phone;
  const email = isProspect ? quote.prospect_email : quote.patients?.email;
  const professionalName = formatProfessionalName(quote.professionals, { withRole: true });
  const total = items.reduce((sum, it) => sum + (Number(it.quoted_price) || 0), 0);

  const infoFields = [
    `<div style="padding:3px 0;"><strong>Para:</strong> ${esc(contactName || '—')}</div>`,
    `<div style="padding:3px 0;"><strong>Fecha:</strong> ${esc(formatDateLong(quote.created_at?.slice(0, 10)))}</div>`,
    rut && `<div style="padding:3px 0;"><strong>RUT:</strong> ${esc(rut)}</div>`,
    quote.valid_until &&
      `<div style="padding:3px 0;"><strong>Válida hasta:</strong> ${esc(formatDateLong(quote.valid_until))}</div>`,
    phone && `<div style="padding:3px 0;"><strong>Teléfono:</strong> ${esc(phone)}</div>`,
    `<div style="padding:3px 0;"><strong>Atendido por:</strong> ${esc(professionalName)}</div>`,
    email && `<div style="padding:3px 0;"><strong>Correo:</strong> ${esc(email)}</div>`,
    `<div style="padding:3px 0;"><strong>Sucursal:</strong> ${esc(quote.branches?.name || '—')}</div>`,
  ]
    .filter(Boolean)
    .join('');

  const itemRows = items
    .map((it) => {
      const c = it.catalog;
      const brandName = c?.brand_ref?.name || c?.brand || '';
      const categoryName = c?.category?.name || '';
      return `
        <tr>
          <td style="padding:8px 9px; border-bottom:1px solid #ddd; width:64px;">
            <div style="width:56px; height:56px; border:1px solid #eee; border-radius:6px; display:flex; align-items:center; justify-content:center; overflow:hidden; background:#fafafa;">
              ${it.imageUrl ? `<img src="${esc(it.imageUrl)}" style="max-width:100%; max-height:100%; object-fit:contain;" />` : '<span style="color:#bbb; font-size:9px;">Sin foto</span>'}
            </div>
          </td>
          <td style="padding:8px 9px; border-bottom:1px solid #ddd;">
            <div style="font-weight:700; font-size:13.5px;">${esc(brandName)} ${esc(c?.model || '')}</div>
            ${categoryName ? `<div style="color:#777; font-size:11px;">${esc(categoryName)}</div>` : ''}
            ${c?.description ? `<div style="font-size:11.5px; color:#444; white-space:pre-line; margin-top:2px;">${esc(c.description)}</div>` : ''}
          </td>
          <td style="padding:8px 9px; border-bottom:1px solid #ddd; text-align:right; font-weight:700; font-size:13.5px; white-space:nowrap; vertical-align:top;">${esc(formatCurrency(it.quoted_price))}</td>
        </tr>
      `;
    })
    .join('');

  const branchContact = [
    quote.branches?.address ? addressHtml(quote.branches.address, quote.branches?.mapsLinkEnabled ?? quote.branches?.maps_link_enabled) : '',
    quote.branches?.phone ? esc(quote.branches.phone) : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return `
    <div style="font-family:'Inter',Arial,sans-serif; padding:10px 4px; width:100%; max-width:210mm; margin:0 auto; font-size:13px;">
      ${letterhead(logoUrl)}
      <h3 style="text-align:center; font-size:14px; text-transform:uppercase; letter-spacing:.05em; border-top:1px solid #ccc; border-bottom:1px solid #ccc; padding:7px 0; margin:10px 0 14px;">Cotización de audífonos</h3>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:0 24px; border:1px solid #ddd; border-radius:6px; padding:10px 14px; margin-bottom:16px; font-size:12.5px;">
        ${infoFields}
      </div>

      <div style="font-weight:700; font-size:12.5px; text-decoration:underline; margin-bottom:6px;">Equipos cotizados</div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:6px;">
        <tbody>${itemRows}</tbody>
        <tfoot>
          <tr>
            <td colspan="2" style="padding:9px; text-align:right; font-weight:700; font-size:13px; border-top:2px solid #333;">Total</td>
            <td style="padding:9px; text-align:right; font-weight:700; font-size:15px; border-top:2px solid #333; white-space:nowrap;">${esc(formatCurrency(total))}</td>
          </tr>
        </tfoot>
      </table>

      ${
        quote.payment_terms || quote.notes
          ? `<div style="margin-top:10px; font-size:12px;">
        ${quote.payment_terms ? `<div style="margin-bottom:4px;"><strong>Condiciones de pago:</strong> ${esc(quote.payment_terms)}</div>` : ''}
        ${quote.notes ? `<div><strong>Notas:</strong> ${esc(quote.notes)}</div>` : ''}
      </div>`
          : ''
      }

      <p style="font-size:12px; color:#555; margin-top:16px; text-align:center; font-style:italic;">
        Esta cotización fue preparada especialmente para usted por nuestro equipo profesional.
      </p>

      <div style="border-top:1px solid #ccc; margin-top:10px; padding-top:8px; font-size:10.5px; color:#777; text-align:center;">
        ${branchContact ? `${branchContact}<br/>` : ''}
        Cotización referencial, no constituye una venta. Los precios y la disponibilidad pueden variar
        ${quote.valid_until ? `después de la fecha de vigencia indicada arriba` : 'sin previo aviso'}.
      </div>
    </div>
  `;
}

// Certificado de garantía de audífono (migración 0052, 16-sep-2026) — pedido
// de Nicolás: un documento imprimible por venta, disponible desde Audífonos
// → Ventas. Mismo criterio visual que la Cotización rediseñada (ancho
// completo de hoja, grilla de datos compacta) — las condiciones de garantía
// (`ha.catalog.warranty_terms`) se configuran por producto en Catálogo, no
// acá; si el producto todavía no las tiene cargadas, se imprime un aviso en
// vez de dejar el bloque vacío. `imageUrl` viene resuelta por el llamador
// (signed URL de Storage, igual que en QuotesList.jsx) — este archivo solo
// arma el HTML. `signatureUrl` (16-sep-2026, pedido de Nicolás): la garantía
// la firma el profesional que hizo la venta con SU firma digital, si la
// tiene cargada en su perfil — mismo criterio ya probado en
// examReport.jsx/prescriptionReport.jsx (imagen de firma O nombre+RUT+línea
// en blanco para firmar a mano, nunca las dos juntas — si hay firma digital
// se asume que ya trae timbre/RUT, mostrar el nombre tipeado además sería
// redundante).
export function hearingaidWarrantyHTML(ha, imageUrl, branch, logoUrl, signatureUrl) {
  const patientName = ha.patients ? formatPatientName(ha.patients) : '—';
  const professionalName = formatProfessionalName(ha.professionals, { withRole: true });
  const isBilateral = ha.side === 'Bilateral';
  const serials = isBilateral
    ? [ha.serial_od ? `OD: ${ha.serial_od}` : '', ha.serial_oi ? `OI: ${ha.serial_oi}` : ''].filter(Boolean).join(' · ')
    : ha.serial || '—';
  const warrantyTerms = ha.catalog?.warranty_terms?.trim();

  const branchContact = [
    branch?.address ? addressHtml(branch.address, branch?.mapsLinkEnabled ?? branch?.maps_link_enabled) : '',
    branch?.phone ? esc(branch.phone) : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return `
    <div style="font-family:'Inter',Arial,sans-serif; padding:10px 4px; width:100%; max-width:210mm; margin:0 auto; font-size:13px;">
      ${letterhead(logoUrl)}
      <h3 style="text-align:center; font-size:14px; text-transform:uppercase; letter-spacing:.05em; border-top:1px solid #ccc; border-bottom:1px solid #ccc; padding:7px 0; margin:10px 0 14px;">Certificado de garantía</h3>

      <div style="display:grid; grid-template-columns:90px 1fr; gap:14px; align-items:start; border:1px solid #ddd; border-radius:6px; padding:12px 14px; margin-bottom:16px;">
        <div style="width:90px; height:90px; border:1px solid #eee; border-radius:6px; display:flex; align-items:center; justify-content:center; overflow:hidden; background:#fafafa;">
          ${imageUrl ? `<img src="${esc(imageUrl)}" style="max-width:100%; max-height:100%; object-fit:contain;" />` : '<span style="color:#bbb; font-size:11px;">Sin foto</span>'}
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:0 24px; font-size:12.5px;">
          <div style="padding:3px 0; grid-column:1 / -1;"><strong>Producto:</strong> ${esc(ha.brand)} ${esc(ha.model)} · ${esc(ha.side)}</div>
          <div style="padding:3px 0;"><strong>N° de serie:</strong> ${esc(serials)}</div>
          <div style="padding:3px 0;"><strong>Paciente:</strong> ${esc(patientName)}</div>
          <div style="padding:3px 0;"><strong>Fecha de venta:</strong> ${esc(formatDateLong(ha.purchase_date))}</div>
          <div style="padding:3px 0;"><strong>Válida hasta:</strong> ${ha.warranty_end ? esc(formatDateLong(ha.warranty_end)) : '—'}</div>
          <div style="padding:3px 0;"><strong>Duración:</strong> ${esc(ha.warranty_months)} meses</div>
          <div style="padding:3px 0;"><strong>Vendido por:</strong> ${esc(professionalName)}</div>
          <div style="padding:3px 0;"><strong>Sucursal:</strong> ${esc(branch?.name || '—')}</div>
        </div>
      </div>

      <div style="font-weight:700; font-size:12.5px; text-decoration:underline; margin-bottom:6px;">Condiciones de la garantía</div>
      ${
        warrantyTerms
          ? `<p style="font-size:12px; color:#333; white-space:pre-line; margin:0 0 16px;">${esc(warrantyTerms)}</p>`
          : `<p style="font-size:12px; color:#999; font-style:italic; margin:0 0 16px;">Este producto todavía no tiene condiciones de garantía cargadas en Catálogo — agrégalas ahí para que aparezcan acá.</p>`
      }

      ${
        signatureUrl
          ? `<div style="margin-top:24px; display:flex; justify-content:center;">
               <img src="${esc(signatureUrl)}" style="max-height:90px; max-width:280px; object-fit:contain; display:block;" />
             </div>`
          : `<div style="margin-top:32px; display:flex; justify-content:center;">
               <div style="text-align:center; width:260px;">
                 <div style="border-top:1px solid #333; padding-top:6px; font-size:12.5px;">${esc(professionalName)}</div>
                 ${ha.professionals?.rut ? `<div style="font-size:11px; color:#444; margin-top:1px;">RUT ${esc(ha.professionals.rut)}</div>` : ''}
                 <div style="font-size:10.5px; color:#666; margin-top:2px; font-weight:700; letter-spacing:.03em;">FIRMA PROFESIONAL</div>
               </div>
             </div>`
      }

      <div style="border-top:1px solid #ccc; margin-top:20px; padding-top:8px; font-size:10.5px; color:#777; text-align:center;">
        ${branchContact ? `${branchContact}<br/>` : ''}
        Conserve este documento — se solicita para hacer efectiva la garantía.
      </div>
    </div>
  `;
}

export function dayAgendaHTML({ date, branchName, module, appointments, isBlockType, logoUrl }) {
  const rows = appointments
    .map((a) => {
      const isBlock = isBlockType(a.type);
      const p = !isBlock ? a.patients : null;
      return `<tr>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(a.time?.slice(0, 5))}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(isBlock ? a.notes || '—' : formatPatientName(p || {}))}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(p?.rut || '—')}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(p?.phone || '—')}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(a.type)}</td>
        <td style="padding:7px 8px; border-bottom:1px solid #ddd;">${esc(formatProfessionalName(a.professionals))}</td>
      </tr>`;
    })
    .join('');

  return `
    <div style="font-family:'Inter',Arial,sans-serif; padding:24px;">
      <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; margin-bottom:2px;">
        <div>${logoUrl ? `<img src="${esc(logoUrl)}" style="max-height:36px; max-width:110px; object-fit:contain;" />` : ''}</div>
        <h2 style="font-family:'Space Grotesk',Arial,sans-serif; margin:0; text-align:center; white-space:nowrap;">AGENDA COMUNAL</h2>
        <div></div>
      </div>
      <div style="color:#666; font-size:12px; margin-bottom:14px; text-align:center;">by GlobalVision · Agenda del día · Módulo ${esc(module)} · ${esc(branchName)}</div>
      <div style="font-weight:600; margin-bottom:12px; text-transform:capitalize;">${esc(formatDateLong(date))}</div>
      <table style="width:100%; border-collapse:collapse; font-size:12.5px;">
        <thead><tr>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">Hora</th>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">Paciente</th>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">RUT</th>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">Teléfono</th>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">Tipo de atención</th>
          <th style="text-align:left; padding:7px 8px; border-bottom:2px solid #333;">Profesional</th>
        </tr></thead>
        <tbody>${rows || '<tr><td colspan="6" style="padding:10px 8px;">Sin citas agendadas este día.</td></tr>'}</tbody>
      </table>
    </div>
  `;
}
